import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useQuery } from '@tanstack/react-query'

import API from '../api/api'
import { selectUserRole } from '../reducers/userDataReducer'
import { selectActiveBuilding } from '../reducers/buildingSelectorReducer'

const createPermissionKey = (buildingId, module, submodule = '', action = '') =>
  `${buildingId}:${module}:${submodule}:${action}`

const createPermissionMap = (permissions) => {
  const map = new Map()
  if (!permissions?.length) return map

  for (const perm of permissions) {
    if (!perm.enabled) continue
    const key = createPermissionKey(
      perm.buildingId,
      perm.module,
      perm.submodule || '',
      perm.action || '',
    )
    map.set(key, perm)
  }
  return map
}

export const useUserPermissions = () => {
  const userRole = useSelector(selectUserRole)
  const buildingData = useSelector(selectActiveBuilding)

  const eligibleForPermissions = useMemo(() => {
    if (!userRole || userRole === 'Client' || userRole === 'Administrator') return false
    if (!buildingData?.flexibleRoleAvailable) return false
    return true
  }, [userRole, buildingData])

  const {
    data,
    error,
    isFetching: loading,
  } = useQuery({
    queryKey: ['getMyPermissions', userRole],
    queryFn: async () => {
      const res = await API.getMyPermissions()
      return {
        data: res.data,
        permissionsMap: createPermissionMap(res.data.permissions),
      }
    },
    enabled: !!userRole && eligibleForPermissions,
  })
  const { permissionsMap } = data || {}

  const useNewPermissions = eligibleForPermissions && !!permissionsMap

  const hasPermissionFor = (buildingId, module, submodule = '', action = '') => {
    if (!useNewPermissions || !permissionsMap) return false
    return permissionsMap.has(createPermissionKey(buildingId, module, submodule, action))
  }

  const hasAnyPermissionFor = (buildingId, permissionList) => {
    if (!useNewPermissions || !permissionsMap) return false
    return permissionList?.some((perm) =>
      hasPermissionFor(buildingId, perm.module, perm.submodule || '', perm.action || ''),
    )
  }

  const hasAllPermissionsFor = (buildingId, permissionList) => {
    if (!useNewPermissions || !permissionsMap) return false
    return permissionList?.every((perm) =>
      hasPermissionFor(buildingId, perm.module, perm.submodule || '', perm.action || ''),
    )
  }

  const hasPermission = (module, submodule = '', action = '') => {
    if (!buildingData?.id) return false
    return hasPermissionFor(buildingData.id, module, submodule, action)
  }

  const hasAnyPermission = (permissionList) => {
    if (!buildingData?.id) return false
    return hasAnyPermissionFor(buildingData.id, permissionList)
  }

  const hasAllPermissions = (permissionList) => {
    if (!buildingData?.id) return false
    return hasAllPermissionsFor(buildingData.id, permissionList)
  }

  return {
    loading,
    error,
    useNewPermissions,
    eligibleForPermissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasPermissionFor,
    hasAnyPermissionFor,
    hasAllPermissionsFor,
  }
}
